import {
  eventEntityRecordId,
  eventEntityRoute,
  eventOccurrences,
  eventSectionItems,
  normalizeEntityKey,
  resolveEventEntityDetails,
} from './entityDetails';

const sections = [
  { key: 'sessions', label: 'Session' },
  { key: 'venues', label: 'Event' },
  { key: 'activities', label: 'Activity' },
  { key: 'places', label: 'Place' },
  { key: 'dining', label: 'Food & Drink' },
];

const hasLongFormStory = (details) => Boolean(
  details?.storySections?.length || details?.storyPages?.length,
);

const textFromStoryItem = (item) => {
  if (typeof item === 'string') return item;
  if (!item || typeof item !== 'object') return '';
  return [item.title, item.heading, item.description, item.text, item.value]
    .filter(Boolean)
    .join(' ');
};

export const hasSubstantiveNarrative = (details) => {
  if (!details) return false;
  const narrativeSections = [
    ...(details.storySections || []),
    ...(details.storyPages || []).flatMap((page) => page.sections || []),
  ];
  const paragraphs = narrativeSections.flatMap((section) => section.paragraphs || []);
  const supportingItems = [
    ...(details.storyItems || []).map((item) => item.value),
    ...narrativeSections.flatMap((section) => section.items || section.bullets || []),
  ];
  const narrativeText = [...paragraphs, ...supportingItems]
    .map(textFromStoryItem)
    .filter(Boolean)
    .join(' ')
    .trim();
  const wordCount = narrativeText.split(/\s+/).filter(Boolean).length;

  if (wordCount < 45) return false;
  if (paragraphs.length >= 3) return true;
  if (narrativeSections.length >= 2 && wordCount >= 60) return true;
  if ((details.storyPages || []).length >= 2 && wordCount >= 60) return true;
  return wordCount >= 100;
};

const eatVenueRoute = (title = '') => {
  const eatSlug = normalizeEntityKey(String(title).replace(/&/g, ' and '));
  return eatSlug ? { name: 'reckon-way-eat', params: { eatSlug } } : null;
};

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const journeyDateForOccurrence = (occurrence = {}) => {
  if (!occurrence.startDate || !occurrence.narrativeDay) return occurrence.startDate;
  const narrativeWeekday = weekdays.find((weekday) => occurrence.narrativeDay.includes(weekday));
  if (!narrativeWeekday) return occurrence.startDate;
  const date = new Date(`${occurrence.startDate}T00:00:00Z`);
  const daysBack = (date.getUTCDay() - weekdays.indexOf(narrativeWeekday) + 7) % 7;
  date.setUTCDate(date.getUTCDate() - daysBack);
  return date.toISOString().slice(0, 10);
};

export const eventStoryTimeline = (event, entityById = {}) => {
  if (!event) return [];

  const seen = new Set();
  const entries = sections.flatMap((section, sectionIndex) => (
    eventSectionItems(event, section.key).flatMap((item, itemIndex) => {
      const entity = entityById[item.entityId] || null;
      const details = resolveEventEntityDetails({
        event,
        sectionKey: section.key,
        item,
        entity,
      });
      if (!hasLongFormStory(details)) return null;
      const hasNarrative = hasSubstantiveNarrative(details);

      const recordId = eventEntityRecordId(item);
      return eventOccurrences(event, item).map((occurrence, occurrenceIndex) => {
        const identity = `${item.timelineId || recordId}-${occurrence.startDate || occurrenceIndex}`;
        if (seen.has(identity)) return null;
        seen.add(identity);
        const baseId = `${section.key}-${recordId}`;
        const mapLocation = [
          details.location,
          entity?.address,
          entity?.city,
          entity?.state,
          entity?.country,
        ].filter(Boolean).filter((value, index, values) => values.indexOf(value) === index).join(', ');
        return {
          id: occurrenceIndex ? `${baseId}-${occurrenceIndex + 1}` : baseId,
          recordId,
          sectionKey: section.key,
          type: section.label,
          title: details.title,
          summary: details.description,
          mapLocation,
          mapUrl: details.mapUrl,
          latitude: item.latitude ?? entity?.latitude,
          longitude: item.longitude ?? entity?.longitude,
          dayLabel: occurrence.narrativeDay || occurrence.displayDate,
          year: item.year || (occurrence.startDate ? Number(occurrence.startDate.slice(0, 4)) : null),
          startDate: occurrence.startDate,
          journeyDate: journeyDateForOccurrence(occurrence),
          hasSequencingTime: Boolean(occurrence.startTime),
          timeLabel: occurrence.displayTime || (occurrence.startTime
            ? `${occurrence.datePrecision === 'approximate' ? 'About ' : ''}${occurrence.startTime}`
            : ''),
          sortTimestamp: occurrence.sortTimestamp || '9999-12-31T23:59:59',
          sequenceOverride: occurrence.sequenceOverride
            ?? (10000 + (sectionIndex * 1000) + itemIndex),
          relationshipOrder: occurrence.relationshipOrder,
          websiteUrl: details.websiteUrl,
          route: hasNarrative ? eventEntityRoute(event, section.key, item) : null,
          venueRoute: section.key === 'dining' ? eatVenueRoute(details.title) : null,
        };
      });
    })
  )).filter(Boolean);

  const chronologyEntries = (event.chronology || []).filter((item) => item.mapTimeline).map((item) => {
    const occurrence = item.occurrence || {};
    const entity = entityById[item.entityId] || null;
    const existingEntry = entries.find((entry) => (
      entry.recordId === item.entityId
      && entry.startDate === occurrence.startDate
    ));
    if (existingEntry) return null;

    const linkedSection = sections.find((section) => (
      eventSectionItems(event, section.key).some((reference) => reference.entityId === item.entityId)
    ));
    const linkedReference = linkedSection
      ? eventSectionItems(event, linkedSection.key).find((reference) => reference.entityId === item.entityId)
      : null;
    const linkedDetails = linkedReference
      ? resolveEventEntityDetails({
        event,
        sectionKey: linkedSection.key,
        item: linkedReference,
        entity,
      })
      : null;

    return {
      id: `chronology-${item.id}`,
      chronologyId: item.id,
      recordId: item.entityId || item.id,
      sectionKey: linkedSection?.key || 'chronology',
      type: item.mapType || 'Conference',
      title: item.title,
      summary: item.note || entity?.shortDescription || '',
      mapLocation: item.mapLocation || [entity?.address, entity?.city, entity?.country].filter(Boolean).join(', '),
      latitude: item.latitude ?? entity?.latitude,
      longitude: item.longitude ?? entity?.longitude,
      dayLabel: occurrence.narrativeDay || occurrence.displayDate,
      year: item.year || (occurrence.startDate ? Number(occurrence.startDate.slice(0, 4)) : null),
      startDate: occurrence.startDate,
      journeyDate: journeyDateForOccurrence(occurrence),
      hasSequencingTime: Boolean(occurrence.startTime),
      sortTimestamp: occurrence.sortTimestamp || '9999-12-31T23:59:59',
      sequenceOverride: occurrence.sequenceOverride ?? 10000,
      relationshipOrder: occurrence.relationshipOrder,
      websiteUrl: linkedDetails?.websiteUrl || item.websiteUrl || entity?.websiteUrl || '',
      route: linkedReference && hasSubstantiveNarrative(linkedDetails)
        ? eventEntityRoute(event, linkedSection.key, linkedReference)
        : null,
      venueRoute: linkedSection?.key === 'dining'
        ? eatVenueRoute(entity?.name || item.title)
        : null,
    };
  }).filter(Boolean);

  entries.push(...chronologyEntries);

  return entries.sort((left, right) => {
    const dateOrder = (left.startDate || '9999-12-31').localeCompare(right.startDate || '9999-12-31');
    if (dateOrder) return dateOrder;
    if (Number.isFinite(left.relationshipOrder) && Number.isFinite(right.relationshipOrder)) {
      return left.relationshipOrder - right.relationshipOrder;
    }
    if (!left.hasSequencingTime || !right.hasSequencingTime) {
      return left.sequenceOverride - right.sequenceOverride
        || left.sortTimestamp.localeCompare(right.sortTimestamp)
        || left.title.localeCompare(right.title);
    }
    return left.sortTimestamp.localeCompare(right.sortTimestamp)
      || left.sequenceOverride - right.sequenceOverride
      || left.title.localeCompare(right.title);
  });
};

export const adjacentEventStories = (event, entityById, sectionKey, recordId) => {
  const stories = eventStoryTimeline(event, entityById).filter((entry) => entry.route);
  const currentIndex = stories.findIndex((entry) => (
    entry.sectionKey === sectionKey && entry.recordId === recordId
  ));

  if (currentIndex < 0) return { previous: null, next: null };
  return {
    previous: stories[currentIndex - 1] || null,
    next: stories[currentIndex + 1] || null,
  };
};

export default eventStoryTimeline;
